import { EventConfirmer } from '~/core/events/EventConfirmer';
import { isCloseEvent } from '~/core/events/types/eventTypes/CloseEvent';
import { isGoToResultEvent } from '~/core/events/types/eventTypes/GoToResultEvent';
import { isOpenEvent } from '~/core/events/types/eventTypes/OpenEvent';
import { isResetQueryEvent } from '~/core/events/types/eventTypes/ResetQueryEvent';
import { isSearchEvent } from '~/core/events/types/eventTypes/SearchEvent';
import { AvailableConfirmationEvents } from '~/core/events/types/helperTypes/availableConfirmationEvents';
import { isSERPEvent, SERPConfirmationEventType, SERPEvent, SERPEventType } from '~/core/events/types/SERPEvents';
import { EventQueue } from '../../core/events/EventQueue';
import { ActionAnalyticsClient } from './ActionAnalyticsClient';
import { ActionAnalyticsUUID } from './ActionAnalyticsUUID';
import { ActionFactory } from './ActionFactory';
import { CommonActionPayload } from './actions/actionTypes';
import { ActionStorage } from './ActionStorage';

/**
 * Custom analytics for SERP
 * AA should handle events: search, go-to-result, open, close
 * for close event, therer are two types: close and close-immediately
 * close-immediately is triggered when user closes serp before doing any action
 * close is triggered when user closes serp after doing any action
 * AA should send requests to API
 * AA should save requests to local storage if API is not available
 * AA should send saved requests to API when it becomes available
 * Incoming events, except for open event are put in to a queue
 * If there is event that needs confirmation (e.g. go-to-result and close) the queue shlould be processed
 */
export class ActionAnalytics {
    private readonly ACTION_ANALYTICS_UUID_EXPIRATION_MINUTES = 5 as const;

    private readonly client: ActionAnalyticsClient;
    private readonly queue: EventQueue;
    private readonly eventConfirmer: EventConfirmer;
    private readonly actionFactory: ActionFactory;
    private readonly actionStorage: ActionStorage;
    private readonly actionAnalyticsUUID: ActionAnalyticsUUID;
    private readonly serpId: string;

    private readonly availableConfirmations = {
        GO_TO_RESULT: SERPConfirmationEventType.GO_TO_RESULT_AA_CONFIRM,
        CLOSE: SERPConfirmationEventType.CLOSE_AA_CONFIRM,
    } as const satisfies AvailableConfirmationEvents;

    constructor(apiUrl: string, serpId: string) {
        this.processEvent = this.processEvent.bind(this);

        this.client = new ActionAnalyticsClient(apiUrl);
        this.queue = new EventQueue(this.processEvent);
        this.eventConfirmer = new EventConfirmer();
        this.actionFactory = new ActionFactory();
        this.actionStorage = new ActionStorage();
        this.actionAnalyticsUUID = new ActionAnalyticsUUID(this.ACTION_ANALYTICS_UUID_EXPIRATION_MINUTES);
        this.actionStorage.sendSavedRequests(this.client.sendAction);
        this.serpId = serpId;
        this.registerEventListeners();
    }

    private registerEventListeners() {
        window.addEventListener(SERPEventType.SEARCH, this.handleSearchEvent);
        window.addEventListener(SERPEventType.GO_TO_RESULT, this.handleGoToResultEvent);
        window.addEventListener(SERPEventType.OPEN, this.handleOpenEvent);
        window.addEventListener(SERPEventType.CLOSE, this.handleCloseEvent);
        window.addEventListener(SERPEventType.RESET_QUERY, this.handleResetQueryEvent);
    }

    /**
     * callback for queue
     */
    private processEvent = async (event: SERPEvent): Promise<void> => {
        if (!isSERPEvent(event)) {
            throw new Error('Event is not SERP event');
        }
        const action = this.actionFactory.createActionFromEvent(event);
        const request = await this.client.sendAction({
            ...this.getCommonActionData(),
            ...action,
        });
        if (!request.delivered) {
            this.actionStorage.saveRequest(request.action);
        }
    };

    /**
     * If reset query event is received, queue should be cleared without processing
     */
    private handleResetQueryEvent = (event: Event): void => {
        if (!isSERPEvent(event) || !isResetQueryEvent(event)) {
            throw new Error('Reset query event does not have correct format');
        }
        this.queue.clearQueue();
    };

    /**
     * Search events should be put in to queue
     */
    private handleSearchEvent = (event: Event): void => {
        if (!isSERPEvent(event) || !isSearchEvent(event)) {
            throw new Error('Search event does not have correct format');
        }
        this.queue.addEvent(event);
        return;
    };

    /**
     * Go-to-result event should be put in to queue and queue should be processed
     * Queue should be processed before sending confirmation
     */
    private handleGoToResultEvent = (event: Event): void => {
        if (!isSERPEvent(event) || !isGoToResultEvent(event)) {
            throw new Error('Go-to-result event does not have correct format');
        }
        (async () => {
            this.queue.addEvent(event);
            await this.queue.processQueue();
            this.eventConfirmer.confirmEventDelivery(
                event.detail.deliveryConfirmations,
                this.availableConfirmations,
                event
            );
        })();
        return;
    };

    /**
     * Open event is simple, just notify AA no mather what
     */
    private handleOpenEvent = (event: Event): void => {
        if (!isSERPEvent(event) || !isOpenEvent(event)) {
            throw new Error('Open event does not have correct format');
        }

        this.client.sendAction({
            ...this.getCommonActionData(),
            ...this.actionFactory.createOpenAction(event),
        });
        return;
    };

    /**
     * Close event should be put in to queue and queue should be processed
     * Queue should be processed before confirming event delivery
     */
    private handleCloseEvent = (event: Event): void => {
        if (!isSERPEvent(event) || !isCloseEvent(event)) {
            throw new Error('Close event does not have correct format');
        }

        if (isSERPEvent(event) && isCloseEvent(event)) {
            (async () => {
                this.queue.addEvent(event);
                await this.queue.processQueue();
                this.eventConfirmer.confirmEventDelivery(
                    event.detail.deliveryConfirmations,
                    this.availableConfirmations,
                    event
                );
            })();
            return;
        }
    };

    private getCommonActionData(): CommonActionPayload {
        return {
            uuId: this.actionAnalyticsUUID.getUUID(),
            dateIssued: this.getCurrentTime(),
            serpId: this.serpId,
        };
    }

    /** Gets local time formatted as "YYYY-MM-DD HH-MM-SS" */
    private getCurrentTime() {
        const localOffsetMiliseconds = new Date().getTimezoneOffset() * 60000;
        return new Date(Date.now() - localOffsetMiliseconds).toISOString().slice(0, -5).replace('T', ' ');
    }
}
